import { Button, Label, Modal, TextInput } from "flowbite-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Client } from "@/sharedTypes";
import { useTranslation } from "react-i18next";

interface ClientFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Client) => void;
  mode?: "create" | "edit";
  initialData?: Client;
}

const ClientFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
}: ClientFormDialogProps) => {
  const { t } = useTranslation(["client", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Client>();

  useEffect(() => {
    if (open) {
        if (initialData) {
            reset(initialData);
        } else {
            reset({ client_username: "", client_email: "", client_phone: "" } as Client);
        }
    }
  }, [initialData, reset, open]);

  const onFormSubmit = (data: Client) => {
    onSubmit(data);
  };

  return (
    <Modal show={open} onClose={onClose} size="md">
      <Modal.Header>{mode === "edit" ? t("client:editClient") : t("client:createClient")}</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="client_username" value={t("client:clientUsername")} />
            </div>
            <TextInput
              id="client_username"
              placeholder={t("client:enterClientUsername")}
              {...register("client_username", { required: t("client:clientUsernameRequired") })}
              color={errors.client_username ? "failure" : undefined}
              helperText={
                errors.client_username && (
                  <span className="font-medium">{errors.client_username.message}</span>
                )
              }
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="client_email" value={t("client:clientEmail")} />
            </div>
            <TextInput
              id="client_email"
              type="email"
              placeholder={t("client:enterClientEmail")}
              {...register("client_email", { 
                  required: t("client:clientEmailRequired"),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: t("client:invalidEmailFormat"),
                  },
              })}
              color={errors.client_email ? "failure" : undefined}
              helperText={
                errors.client_email && (
                  <span className="font-medium">{errors.client_email.message}</span>
                )
              }
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="client_phone" value={t("client:clientPhone")} />
            </div>
            <TextInput
              id="client_phone"
              placeholder={t("client:enterClientPhone")}
              {...register("client_phone")}
              color={errors.client_phone ? "failure" : undefined}
              helperText={
                errors.client_phone && (
                  <span className="font-medium">{errors.client_phone.message}</span>
                )
              }
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button color="failure" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit">
              {mode === "edit" ? t("common:saveChanges") : t("client:createClient")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ClientFormDialog;
